// services/staffService.ts
import { ref, update, get, set, remove, onValue, Unsubscribe, query, orderByChild, equalTo } from 'firebase/database';
import { db } from '../config/firebase';

/**
 * Fetch all NON-PLAYER user records via indexed accountType queries.
 *
 * /users holds staff AND every player/parent/child account, so a plain
 * get('/users') downloads a multi-MB tree on a big event (1,500+ players)
 * when dashboards only ever need the handful of staff records. accountType
 * is indexed (database.rules.json users.indexOn), so each query below is
 * server-filtered and returns only its slice. The `null` query picks up
 * legacy records created before accountType existed.
 */
export async function fetchStaffUsers(): Promise<Record<string, Record<string, unknown>>> {
  const types: Array<string | null> = ['staff', 'admin', 'store', 'user', null];
  const snaps = await Promise.all(
    types.map((t) =>
      get(query(ref(db, 'users'), orderByChild('accountType'), equalTo(t)))
        .catch(() => null),
    ),
  );
  const out: Record<string, Record<string, unknown>> = {};
  for (const s of snaps) {
    if (!s) continue;
    const v = (s.val() || {}) as Record<string, Record<string, unknown>>;
    Object.assign(out, v);
  }
  return out;
}

/**
 * LIVE variant: subscribe to the user slices for the given accountTypes via
 * indexed queries (one listener per type, server-filtered) and emit the
 * merged map on every change. Replaces whole-`/users` onValue subscriptions,
 * which re-downloaded every player/parent/child record on any user write.
 * Pass `null` in `types` to include legacy records with no accountType.
 */
export function subscribeUsersByTypes(
  types: Array<string | null>,
  callback: (users: Record<string, Record<string, unknown>>) => void,
): Unsubscribe {
  const slices = new Map<string | null, Record<string, Record<string, unknown>>>();
  const emit = () => {
    const merged: Record<string, Record<string, unknown>> = {};
    for (const slice of slices.values()) Object.assign(merged, slice);
    callback(merged);
  };
  const unsubs = types.map((t) =>
    onValue(
      query(ref(db, 'users'), orderByChild('accountType'), equalTo(t)),
      (snap) => {
        slices.set(t, (snap.val() || {}) as Record<string, Record<string, unknown>>);
        emit();
      },
      () => {
        slices.set(t, {});
        emit();
      },
    ),
  );
  return () => unsubs.forEach((u) => u());
}

/** The accountType slices that make up "staff-like" accounts. */
export const STAFF_ACCOUNT_TYPES: Array<string | null> = ['staff', 'admin', 'store', 'user', null];

/** Store accounts only (indexed query) — for store pickers/dialogs. */
export async function fetchStoreAccounts(): Promise<Record<string, Record<string, unknown>>> {
  const snap = await get(query(ref(db, 'users'), orderByChild('accountType'), equalTo('store')));
  return (snap.val() || {}) as Record<string, Record<string, unknown>>;
}

// Types
export interface StaffMember {
  odooId: string;
  firstName: string;
  role: string;
  onBreak: boolean;
  assignedAt: number;
  breakStartTime?: number | null;
  lastBreakEnd?: number | null;
}

export interface StaffAssignInput {
  odooId?: string;
  id?: string;
  firstName?: string;
  name?: string;
  role: string;
}

export interface StaffStats {
  total: number;
  available: number;
  onBreak: number;
  byRole: Record<string, number>;
}

export interface AvailableStaff {
  odooId: string;
  firstName?: string;
  role?: string;
  approved: boolean;
  [key: string]: unknown;
}

type StaffCallback = (staff: StaffMember[]) => void;

/**
 * Staff Service - Handles all staff-related operations
 */
export const staffService = {
  /**
   * Get all staff assigned to a tournament
   */
  async getByTournament(tournamentId: string): Promise<StaffMember[]> {
    const snapshot = await get(ref(db, `tournaments/${tournamentId}/staff`));
    const data = snapshot.val() || {};

    return Object.entries(data).map(([odooId, staffData]) => ({
      ...(staffData as Omit<StaffMember, 'odooId'>),
      odooId,
    }) as StaffMember);
  },

  /**
   * Get all available staff from staffDirectory (lightweight collection)
   * This collection is auto-synced by Cloud Functions from users.
   * ~50KB instead of ~2MB when fetching all users.
   */
  async getAllAvailable(): Promise<AvailableStaff[]> {
    const snapshot = await get(ref(db, 'staffDirectory'));
    const data = snapshot.val() || {};

    return Object.entries(data).map(([odooId, staffData]) => {
      const staff = staffData as {
        firstName?: string;
        lastName?: string;
        email?: string;
        accountType?: string;
        languages?: string[];
        certifications?: string[];
      };
      return {
        odooId,
        firstName: staff.firstName,
        role: staff.accountType || 'staff',  // accountType is the new system
        approved: true,  // If in staffDirectory, they're approved
        ...staff,
      } as AvailableStaff;
    });
  },

  /**
   * Get all available staff (legacy method - falls back to users if staffDirectory empty)
   * Use getAllAvailable() for optimized fetches.
   */
  async getAllAvailableLegacy(): Promise<AvailableStaff[]> {
    // Try staffDirectory first
    const staffDirSnapshot = await get(ref(db, 'staffDirectory'));
    if (staffDirSnapshot.exists() && Object.keys(staffDirSnapshot.val()).length > 0) {
      return this.getAllAvailable();
    }

    // Fall back to users collection (legacy) — staff-type slices only, so
    // the fallback never downloads the full player roster.
    const data = await fetchStaffUsers();

    return Object.entries(data)
      .filter(([_, user]) => {
        const userData = user as { approved?: boolean; role?: string; accountType?: string };
        // Check new accountType OR legacy approved
        return (userData.accountType === 'staff' || userData.accountType === 'admin') ||
          (userData.approved && userData.role !== 'pending');
      })
      .map(([odooId, userData]) => ({
        ...(userData as Omit<AvailableStaff, 'odooId'>),
        odooId,
      }) as AvailableStaff);
  },

  /**
   * Assign staff to a tournament
   */
  async assign(tournamentId: string, staffMember: StaffAssignInput): Promise<void> {
    const odooId = staffMember.odooId || staffMember.id;

    if (!odooId) {
      throw new Error('Staff member ID is required');
    }

    await set(ref(db, `tournaments/${tournamentId}/staff/${odooId}`), {
      firstName: staffMember.firstName || staffMember.name,
      role: staffMember.role,
      onBreak: false,
      assignedAt: Date.now(),
    });
  },

  /**
   * Unassign staff from a tournament
   */
  async unassign(tournamentId: string, odooId: string): Promise<void> {
    await remove(ref(db, `tournaments/${tournamentId}/staff/${odooId}`));
  },

  /**
   * Assign multiple staff members at once
   */
  async assignMultiple(tournamentId: string, staffMembers: StaffAssignInput[]): Promise<void> {
    const updates: Record<string, Partial<StaffMember>> = {};

    staffMembers.forEach(member => {
      const odooId = member.odooId || member.id;
      if (odooId) {
        updates[odooId] = {
          firstName: member.firstName || member.name || '',
          role: member.role,
          onBreak: false,
          assignedAt: Date.now(),
        };
      }
    });

    await update(ref(db, `tournaments/${tournamentId}/staff`), updates);
  },

  /**
   * Update staff member's break status
   */
  async updateBreak(tournamentId: string, odooId: string, onBreak: boolean): Promise<void> {
    await update(ref(db, `tournaments/${tournamentId}/staff/${odooId}`), {
      onBreak,
      breakStartTime: onBreak ? Date.now() : null,
      lastBreakEnd: onBreak ? null : Date.now(),
    });
  },

  /**
   * Toggle break status
   */
  async toggleBreak(tournamentId: string, odooId: string): Promise<void> {
    const snapshot = await get(ref(db, `tournaments/${tournamentId}/staff/${odooId}`));
    const staffData = snapshot.val() as StaffMember | null;

    if (staffData) {
      await this.updateBreak(tournamentId, odooId, !staffData.onBreak);
    }
  },

  /**
   * End all breaks for a tournament
   */
  async endAllBreaks(tournamentId: string): Promise<void> {
    const staff = await this.getByTournament(tournamentId);
    const updates: Record<string, unknown> = {};

    staff.forEach(member => {
      if (member.onBreak) {
        updates[`${member.odooId}/onBreak`] = false;
        updates[`${member.odooId}/breakStartTime`] = null;
        updates[`${member.odooId}/lastBreakEnd`] = Date.now();
      }
    });

    if (Object.keys(updates).length > 0) {
      await update(ref(db, `tournaments/${tournamentId}/staff`), updates);
    }
  },

  /**
   * Update staff member's role
   */
  async updateRole(tournamentId: string, odooId: string, role: string): Promise<void> {
    await update(ref(db, `tournaments/${tournamentId}/staff/${odooId}`), {
      role,
    });
  },

  /**
   * Subscribe to tournament staff
   */
  subscribe(tournamentId: string, callback: StaffCallback): Unsubscribe {
    const staffRef = ref(db, `tournaments/${tournamentId}/staff`);

    return onValue(staffRef, (snapshot) => {
      const data = snapshot.val() || {};
      const staff = Object.entries(data).map(([odooId, staffData]) => ({
        odooId,
        ...(staffData as Omit<StaffMember, 'odooId'>),
      }));
      callback(staff);
    });
  },

  /**
   * Get staff statistics for a tournament
   */
  async getStats(tournamentId: string): Promise<StaffStats> {
    const staff = await this.getByTournament(tournamentId);

    const stats: StaffStats = {
      total: staff.length,
      available: 0,
      onBreak: 0,
      byRole: {},
    };

    staff.forEach(member => {
      if (member.onBreak) {
        stats.onBreak++;
      } else {
        stats.available++;
      }

      if (!stats.byRole[member.role]) {
        stats.byRole[member.role] = 0;
      }
      stats.byRole[member.role]++;
    });

    return stats;
  },

  /**
   * Clear all staff from a tournament
   */
  async clearAll(tournamentId: string): Promise<void> {
    await set(ref(db, `tournaments/${tournamentId}/staff`), null);
  },
};

export default staffService;
